import MiniCards from "@/components/MiniCards/MiniCards";
import {
  TWRComposition,
  TWROwner,
  TWRPortfolio,
} from "@/services/WealthReaderService";
import {
  DATA_RECEIVED_COMPOSITION_HEADERS,
  DATA_RECEIVED_EMPTY_MESSAGES,
  DATA_RECEIVED_TITLES,
  PLANES_EXTERNOS_OWNERS_PREFIX,
} from "../../Texts/PlanesExternos";
import {
  buildPortfolioSections,
  formatCurrency,
  formatPercent,
  formatPortfolioSubtype,
  formatShares,
  formatText,
} from "./FormatWRData";

const BASE_CLASS = "planesExternosDataReceived";

type TPlanesExternosDataReceivedProps = {
  wRData: TWRPortfolio[];
};

const PlanesExternosDataReceived: React.FC<
  TPlanesExternosDataReceivedProps
> = ({ wRData }) => {
  const localTheme = window.localStorage.getItem("theme") || "light";

  const portfolios: TWRPortfolio[] = wRData ?? [];

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}__section`}>
        <span className={`${BASE_CLASS}__sectionTitle`}>
          {DATA_RECEIVED_TITLES.portfolios}
        </span>

        {portfolios.length === 0 && (
          <p className={`${BASE_CLASS}__empty`}>
            {DATA_RECEIVED_EMPTY_MESSAGES.noPortfolios}
          </p>
        )}

        {portfolios.map((portfolio: TWRPortfolio, portfolioIndex: number) => (
          <div
            key={portfolio.uuid || `${portfolio.code}-${portfolioIndex}`}
            className={`${BASE_CLASS}__portfolio ${BASE_CLASS}__${localTheme}`}
          >
            <div className={`${BASE_CLASS}__portfolioHeader`}>
              <h4 className={`${BASE_CLASS}__portfolioName`}>
                {formatText(portfolio.name)}
              </h4>
              <div className={`${BASE_CLASS}__portfolioMeta`}>
                <span className={`${BASE_CLASS}__badge`}>
                  {formatPortfolioSubtype(portfolio.subtype)}
                </span>
                <span className={`${BASE_CLASS}__code`}>
                  {formatText(portfolio.code)}
                </span>
              </div>
            </div>

            <MiniCards sections={buildPortfolioSections(portfolio)} />

            {portfolio.owners && portfolio.owners.length > 0 && (
              <p className={`${BASE_CLASS}__owners`}>
                {`${PLANES_EXTERNOS_OWNERS_PREFIX} `}
                {portfolio.owners
                  .map((owner: TWROwner) => formatText(owner.name))
                  .join(", ")}
              </p>
            )}

            <div className={`${BASE_CLASS}__tableContainer`}>
              <span className={`${BASE_CLASS}__tableTitle`}>
                {DATA_RECEIVED_TITLES.composition}
              </span>

              {(portfolio.composition ?? []).length === 0 ? (
                <p className={`${BASE_CLASS}__empty`}>
                  {DATA_RECEIVED_EMPTY_MESSAGES.noComposition}
                </p>
              ) : (
                <table className={`${BASE_CLASS}__table`}>
                  <thead>
                    <tr>
                      <th>{DATA_RECEIVED_COMPOSITION_HEADERS.isin}</th>
                      <th>{DATA_RECEIVED_COMPOSITION_HEADERS.currency}</th>
                      <th className={`${BASE_CLASS}__numeric`}>
                        {DATA_RECEIVED_COMPOSITION_HEADERS.weighting}
                      </th>
                      <th className={`${BASE_CLASS}__numeric`}>
                        {DATA_RECEIVED_COMPOSITION_HEADERS.shares}
                      </th>
                      <th className={`${BASE_CLASS}__numeric`}>
                        {DATA_RECEIVED_COMPOSITION_HEADERS.price}
                      </th>
                      <th className={`${BASE_CLASS}__numeric`}>
                        {DATA_RECEIVED_COMPOSITION_HEADERS.totalAmount}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(portfolio.composition ?? []).map(
                      (position: TWRComposition, positionIndex: number) => (
                        <tr
                          key={`${position.isin ?? position.dgs ?? "position"}-${positionIndex}`}
                        >
                          <td>
                            <h4>{formatText(position.isin ?? position.dgs)}</h4>
                            <p>
                              {" "}
                              {formatText(
                                position.isiN_description ??
                                  position.dgS_description,
                              )}
                            </p>
                          </td>
                          <td>
                            {formatText(
                              position.currency ?? portfolio.currency,
                            )}
                          </td>
                          <td className={`${BASE_CLASS}__numeric`}>
                            {formatPercent(position.weighting)}
                          </td>
                          <td className={`${BASE_CLASS}__numeric`}>
                            {formatShares(position.shares)}
                          </td>
                          <td className={`${BASE_CLASS}__numeric`}>
                            {formatCurrency(
                              position.price,
                              position.currency ?? portfolio.currency,
                            )}
                          </td>
                          <td className={`${BASE_CLASS}__numeric`}>
                            {formatCurrency(
                              position.total_amount,
                              position.currency ?? portfolio.currency,
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanesExternosDataReceived;
